import { Map, Set } from 'immutable';
import { ClipFragment } from '../../components/fragments.generated';
import { Highlights } from '../../components/StudioClipUserList/hooks/useStudioClips/utils/constants';

export function instanceOfCompletedClip(
  obj: ClipObject | ClipObjectPending
): obj is ClipObject {
  return !!obj.isComplete;
}
export function instanceOfFailedPendingClip(
  obj: ClipObject | ClipObjectPending
): obj is ClipObjectPending {
  return !obj.isComplete && !!obj.failedWithError;
}

export type ClipObject = ClipFragment & {
  highlights?: Highlights;
};
// actorVariant is omitted given when we call addPendingClip we do not pass it
// localSrc is for completed clips recently loaded
// audioFormat is not part of a pending clip
// src is only for existing clips
export type ClipObjectPending = Omit<
  ClipFragment,
  'actorVariantId' | 'localSrc' | 'audioFormat' | 'src'
>;

// Leaving for future reference. Immutable.js was not playing very well with defined typescript
// types/interfaces. The difficulty is that we would have to define these types over the entire
// Map interface for each Map type we use (set, get, delete, etc..)
// Relevant issue where this solution was found:
// https://github.com/immutable-js/immutable-js/issues/683#issuecomment-339449483
//
// interface ClipMap extends Map<string, any> {
//     toJS(): ClipObject;
//     get<K extends keyof ClipObject>(key: K): ClipObject[K];
// }

export type ContextState = {
  clips: Map<string, ClipObject | ClipObjectPending>;
  selectedClipIds: Set<string>;
  lastMovedClipDestinationProjectId?: string;
  lastUsedCombineClipMsGapDuration?: number;
  fetching: boolean;
  error?: Error;
};

type UpdatableClipProperties = Pick<ClipObject, 'isCreditableBy'>;
export interface ContextActions {
  addClips: (clips: Array<ClipObject>) => void;
  addPendingClip: (clip: ClipObjectPending) => void;
  updatePendingClip: (id: string, values: Partial<ClipObject>) => void;
  renameClip: (clip: ClipObject, newName: string) => void;
  deleteClip: (clip: ClipObject) => void;
  updateClips: <T extends keyof UpdatableClipProperties>(
    id: string | string[],
    property: T,
    value: UpdatableClipProperties[T]
  ) => void;
  updateClipOrdering: (inputs: {
    clip: ClipObject;
    newIndex: number;
    precedingClip?: ClipObject;
  }) => void;
  updateClipProject: (
    clip: ClipObject,
    newProjectId: string,
    index: number,
    sequence: string
  ) => void;
  setFetchLoadingState: (loading: boolean) => void;
  setFetchError: (error: Error) => void;
  setLastMovedClipDestinationProjectId: (projectId: string) => void;
  setLastUsedCombineClipMsGapDuration: (duration: number) => void;
  setSelectedClipIds: (clipIds: Set<string>) => void;
}

export type ContextProps = [ContextState, ContextActions];

export interface Clips {
  ClipObjectPending: ClipObjectPending;
  ClipObject: ClipObject;
  ContextState: ContextState;
  ContextActions: ContextActions;
  ContextProps: ContextProps;
}
